/* eslint-disable indent */
import {
  Button,
  Divider,
  Grid,
  makeStyles,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import DatePicker from 'components/DatePicker';
import ToggleCard from 'components/ToggleCard';
import i18n from 'i18n';
import React from 'react';
import PersonAddDisabledIcon from '@material-ui/icons/PersonAddDisabled';
import clsx from 'clsx';
import IconButton from '@material-ui/core/IconButton';
import {
  CompanyData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import { inAdminView, inSuperAdminView, inOperatorView } from 'utils/role';
import { format } from 'date-fns';
import styles from '../styles';

const useStyles = makeStyles(styles);

const TIME_FORMAT = 'HH:mm';
const HHMM_REGEX = /^([01]?\d|2[0-3]):[0-5]\d$/;
const HHMMSS_REGEX = /^([01]?\d|2[0-3]):[0-5]\d:[0-5]\d$/;

function normalizeTime(value: string): string {
  if (!value || value === 'Invalid Date') return '';
  const trimmed = value.trim();
  if (HHMM_REGEX.test(trimmed)) return trimmed;
  if (HHMMSS_REGEX.test(trimmed)) return trimmed.substring(0, 5);
  return trimmed;
}

function isValidTimeString(value: string): boolean {
  if (!value || value === 'Invalid Date') return false;
  return HHMM_REGEX.test(value) || HHMMSS_REGEX.test(value);
}

function hasDuplicates(arr: string[]): boolean {
  const normalized = arr
    .map(normalizeTime)
    .filter(t => t && t !== 'Invalid Date' && HHMM_REGEX.test(t));
  return new Set(normalized).size !== normalized.length;
}

export interface RemoveSessionsFormDisplay {
  enabled: boolean;
  session_removal_times_weekdays: string[];
  session_removal_times_saturday: string[];
  session_removal_times_sunday: string[];
  session_removal_max_limit?: number | null | string;
  session_removal_message?: string;
}

export const removeSessionsInitialValues: RemoveSessionsFormDisplay = {
  enabled: false,
  session_removal_times_weekdays: [],
  session_removal_times_saturday: [],
  session_removal_times_sunday: [],
  session_removal_max_limit: null,
  session_removal_message: '',
};

export interface Props {
  defaultValue: RemoveSessionsFormDisplay | null;
  companySelected: CompanyData | null;
  role?: RolesEnum;
  permissions: PermissionsData | undefined;
  onChange: (values: RemoveSessionsFormDisplay) => void;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
}

type PeriodKey =
  | 'session_removal_times_weekdays'
  | 'session_removal_times_saturday'
  | 'session_removal_times_sunday';

const PERIOD_KEYS: { key: PeriodKey; labelKey: string }[] = [
  {
    key: 'session_removal_times_weekdays',
    labelKey: 'SESSION_REMOVAL_TIMES_WEEKDAYS_KEY',
  },
  {
    key: 'session_removal_times_saturday',
    labelKey: 'SESSION_REMOVAL_TIMES_SATURDAY_KEY',
  },
  {
    key: 'session_removal_times_sunday',
    labelKey: 'SESSION_REMOVAL_TIMES_SUNDAY_KEY',
  },
];

const RemoveSessionsForm = ({
  defaultValue,
  role,
  companySelected,
  permissions,
  onChange,
  setHasError,
}: Props) => {
  const classes = useStyles();
  const [values, setValues] = React.useState<RemoveSessionsFormDisplay>(
    removeSessionsInitialValues
  );

  const handleChange = (
    newValues: RemoveSessionsFormDisplay,
    toggle?: boolean
  ) => {
    const updated =
      typeof toggle === 'boolean'
        ? { ...newValues, enabled: toggle }
        : newValues;
    setValues(updated);
    onChange(updated);
  };

  const updateTimes = (periodKey: PeriodKey, newTimes: string[]) => {
    handleChange({ ...values, [periodKey]: newTimes });
  };

  const addTime = (periodKey: PeriodKey) => {
    const current = values[periodKey] || [];
    updateTimes(periodKey, [...current, '']);
  };

  const removeTime = (periodKey: PeriodKey, index: number) => {
    const current = values[periodKey] || [];
    updateTimes(
      periodKey,
      current.filter((_, i) => i !== index)
    );
  };

  const setTimeAt = (
    periodKey: PeriodKey,
    index: number,
    timeValue: string
  ) => {
    const current = [...(values[periodKey] || [])];
    const normalized = timeValue
      ? normalizeTime(timeValue) || timeValue
      : timeValue;
    if (normalized && isValidTimeString(normalized)) {
      const others = current.filter((_, i) => i !== index).map(normalizeTime);
      if (others.includes(normalized)) return;
    }
    current[index] = normalized;
    updateTimes(periodKey, current);
  };

  React.useEffect(() => {
    if (defaultValue) {
      const norm = (arr: string[] | undefined) =>
        (arr || []).map(t => (t ? normalizeTime(t) || t : t));
      setValues({
        ...defaultValue,
        session_removal_times_weekdays: norm(
          defaultValue.session_removal_times_weekdays
        ),
        session_removal_times_saturday: norm(
          defaultValue.session_removal_times_saturday
        ),
        session_removal_times_sunday: norm(
          defaultValue.session_removal_times_sunday
        ),
      });
    }
  }, [defaultValue]);

  const hasInvalidTime = (arr: string[]) =>
    arr.some(t => !isValidTimeString(t) && t !== '');

  const computeError = (): boolean => {
    if (!values.enabled) return false;

    const {
      session_removal_times_weekdays,
      session_removal_times_saturday,
      session_removal_times_sunday,
    } = values;

    const atLeastOnePeriodHasValidTime =
      session_removal_times_weekdays.some(isValidTimeString) ||
      session_removal_times_saturday.some(isValidTimeString) ||
      session_removal_times_sunday.some(isValidTimeString);
    if (!atLeastOnePeriodHasValidTime) return true;

    const invalid =
      hasInvalidTime(session_removal_times_weekdays) ||
      hasInvalidTime(session_removal_times_saturday) ||
      hasInvalidTime(session_removal_times_sunday);
    if (invalid) return true;

    const duplicates =
      hasDuplicates(session_removal_times_weekdays) ||
      hasDuplicates(session_removal_times_saturday) ||
      hasDuplicates(session_removal_times_sunday);
    if (duplicates) return true;

    const maxLimitInvalid =
      values.session_removal_max_limit === null ||
      values.session_removal_max_limit === '' ||
      (typeof values.session_removal_max_limit === 'number' &&
        values.session_removal_max_limit < 0);
    return !!maxLimitInvalid;
  };

  React.useEffect(() => {
    setHasError(computeError());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const isDisabled =
    !!permissions && !permissions?.edit_env_session_removal_instances;

  const renderPeriodSection = (periodKey: PeriodKey, labelKey: string) => {
    const times = values[periodKey] || [];

    return (
      <Grid
        item
        xs={12}
        className={classes.sessionRemovalPeriodBlock}
        key={periodKey}
      >
        <Typography className={classes.maintenanceTitle} variant="subtitle2">
          {i18n.t(labelKey)}
        </Typography>
        {times?.map((timeStr, index) => (
          <div
            key={`${periodKey}-${index}`}
            className={classes.sessionRemovalTimeRow}
          >
            <DatePicker
              disabled={isDisabled}
              id={`${periodKey}-${index}`}
              type="time"
              format={TIME_FORMAT}
              label={i18n.t('REMOVE_SESSIONS_TIME_KEY')}
              value={
                timeStr && isValidTimeString(timeStr)
                  ? `2000-01-01T${normalizeTime(timeStr)}:00`
                  : null
              }
              error={
                timeStr === 'Invalid Date' ||
                (!!timeStr && !isValidTimeString(timeStr))
              }
              keyValue={`${periodKey}-${index}`}
              setFieldsValue={(_key, value) => {
                if (
                  typeof value === 'object' &&
                  String(value) !== 'Invalid Date' &&
                  value !== null
                ) {
                  const date = new Date(String(value));
                  setTimeAt(periodKey, index, format(date, TIME_FORMAT));
                } else if (String(value) === 'Invalid Date' || value === null) {
                  setTimeAt(periodKey, index, 'Invalid Date');
                }
              }}
            />
            <Tooltip title={i18n.t('SESSION_REMOVAL_REMOVE_TIME_KEY')}>
              <span>
                <IconButton
                  size="small"
                  onClick={() => removeTime(periodKey, index)}
                  disabled={isDisabled || times.length === 0}
                  aria-label={i18n.t('SESSION_REMOVAL_REMOVE_TIME_KEY')}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        ))}
        <Button
          size="small"
          variant="outlined"
          onClick={() => addTime(periodKey)}
          disabled={isDisabled}
          style={{ marginTop: 4 }}
        >
          {i18n.t('SESSION_REMOVAL_ADD_TIME_KEY')}
        </Button>
      </Grid>
    );
  };

  return (
    <ToggleCard
      active={values.enabled}
      id="toggle-remove-sessions"
      title={i18n.t('REMOVE_USER_SESSIONS_KEY')}
      getActive={(value: boolean) => handleChange(values, !value)}
      icon={
        <PersonAddDisabledIcon
          className={clsx({
            [classes.iconGreen]: inAdminView(role, companySelected),
            [classes.iconGray]: inSuperAdminView(role, companySelected),
            [classes.iconBlue]: inOperatorView(role, companySelected),
            [classes.iconYellow]: companySelected,
          })}
        />
      }
      disable={isDisabled}
    >
      <Grid container className={classes.FormContainer}>
        {values.enabled &&
          !(
            values.session_removal_times_weekdays?.some(isValidTimeString) ||
            values.session_removal_times_saturday?.some(isValidTimeString) ||
            values.session_removal_times_sunday?.some(isValidTimeString)
          ) && (
            <Grid item xs={12} className={classes.sessionRemovalAlert}>
              <Alert severity="warning">
                {i18n.t('SESSION_REMOVAL_AT_LEAST_ONE_TIME_REQUIRED_KEY')}
              </Alert>
            </Grid>
          )}
        {PERIOD_KEYS.map(({ key, labelKey }) =>
          renderPeriodSection(key, labelKey)
        )}

        <Grid item xs={12} className={classes.sessionRemovalDivider}>
          <Divider />
        </Grid>

        <Grid item className={classes.sessionRemovalMaxLimit}>
          <TextField
            id="session-removal-max-limit"
            label={i18n.t('SESSION_REMOVAL_MAX_LIMIT_KEY')}
            type="number"
            inputProps={{ min: 0 }}
            variant="outlined"
            defaultValue={2}
            value={
              values.session_removal_max_limit === null ||
              typeof values.session_removal_max_limit === 'undefined'
                ? ''
                : String(values.session_removal_max_limit)
            }
            onChange={e => {
              const { value } = e.target;
              const parsed =
                value === '' ? null : Math.max(0, parseInt(value, 10));
              handleChange({
                ...values,
                session_removal_max_limit: parsed,
              });
            }}
            fullWidth
            disabled={isDisabled}
          />
          <Tooltip
            className={classes.tooltipTitle}
            placement="right"
            arrow
            title={i18n.t('REMOVE_SESSIONS_TOOLTIP_KEY')}
          >
            <InfoIcon fontSize="small" color="action" />
          </Tooltip>
        </Grid>

        <Grid item className={classes.removalMessage}>
          <TextField
            id="session-removal-message"
            label={i18n.t('ADD_MESSAGE_REMOVE_SESSION_KEY')}
            value={values.session_removal_message ?? ''}
            variant="outlined"
            onChange={e =>
              handleChange({
                ...values,
                session_removal_message: e.target.value,
              })
            }
            fullWidth
            disabled={isDisabled}
            multiline
          />
        </Grid>
      </Grid>
    </ToggleCard>
  );
};

export default RemoveSessionsForm;
